import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, ChevronRight, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import TimezoneSelect from "@/components/TimezoneSelect";
import { detectTimezone } from "@/lib/timezones";
import { submitBooking } from "@/lib/api";

const TOPICS = [
    "Get a life insurance quote",
    "Review existing policy",
    "Medicare / Health",
    "Annuities",
    "Recruiting interest",
    "Other",
];

const WINDOWS = [
    "Morning 9–12",
    "Early afternoon 12–3",
    "Late afternoon 3–6",
    "Evening 6–8",
];

function detectMinDate() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
}
function getMinDate() {
    return detectMinDate();
}
function getMaxDate() {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
}

export default function Book() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        preferred_date: "",
        preferred_window: WINDOWS[0],
        timezone: detectTimezone(),
        topic: TOPICS[0],
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const sessionId = useMemo(() => {
        try {
            return localStorage.getItem("mm_jarvis_session") || null;
        } catch {
            return null;
        }
    }, []);

    const minD = getMinDate();
    const maxD = getMaxDate();

    const setField = (k, v) => {
        setForm((s) => ({ ...s, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validateLocal = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Required.";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
        const digits = (form.phone || "").replace(/\D+/g, "");
        if (digits.length < 10) e.phone = "Enter a valid 10+ digit phone.";
        if (!form.preferred_date) e.preferred_date = "Pick a date.";
        else if (form.preferred_date < minD || form.preferred_date > maxD)
            e.preferred_date = "Date must be between tomorrow and 30 days out.";
        if ((form.notes || "").length > 500) e.notes = "Keep notes under 500 characters.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validateLocal()) return;
        setSubmitting(true);
        try {
            await submitBooking({
                ...form,
                session_id: sessionId || undefined,
            });
            setSubmitted(true);
            try { localStorage.removeItem("mm_jarvis_session"); } catch { /* noop */ }
        } catch (err) {
            const detail = err?.response?.data;
            if (detail?.fields) {
                const map = {};
                detail.fields.forEach((f) => { map[f.field] = f.message; });
                setErrors(map);
                toast.error("Please fix the highlighted fields.");
            } else if (err?.response?.status === 429) {
                toast.error("Too many requests — please try again in an hour.");
            } else {
                toast.error("Couldn't submit your request. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!form.preferred_date) setField("preferred_date", minD);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="book-page">
            <SEO
                title="Book a call — Moving Mountains Advisors"
                description="Pick a time that works for you and an advisor will personally confirm your appointment. No pressure, no auto-bookings."
            />
            <PageHeader
                eyebrow="Book"
                title="Book a call with an advisor."
                subtitle="Pick a window that works. We'll review and confirm — a real person, not a calendar bot."
            />

            <section className="mm-section bg-white">
                <div className="mm-container grid gap-10 lg:grid-cols-[1.4fr_1fr]">
                    {submitted ? (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mm-card flex flex-col items-start gap-4 p-10"
                            data-testid="book-success"
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                                <CalendarCheck size={22} />
                            </div>
                            <h2 className="mm-display text-3xl text-mm-navy">
                                We'll review and confirm your appointment.
                            </h2>
                            <p className="text-mm-muted">
                                You'll get an email from a Moving Mountains advisor within
                                one business day. If your timing changes, just reply.
                            </p>
                            <a href="/" className="mm-btn-pill mt-2 inline-flex items-center gap-1.5 bg-mm-navy text-white hover:bg-mm-blue">
                                Back home <ChevronRight size={16} />
                            </a>
                        </motion.div>
                    ) : (
                        <form onSubmit={onSubmit} className="mm-card p-7 sm:p-9" data-testid="book-form" noValidate>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <Field label="Full name *" error={errors.name}>
                                    <input
                                        value={form.name}
                                        onChange={(e) => setField("name", e.target.value)}
                                        className="mm-input"
                                        autoComplete="name"
                                        data-testid="book-name"
                                        required
                                    />
                                </Field>
                                <Field label="Email *" error={errors.email}>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setField("email", e.target.value)}
                                        className="mm-input"
                                        autoComplete="email"
                                        data-testid="book-email"
                                        required
                                    />
                                </Field>
                                <Field label="Phone *" error={errors.phone}>
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={(e) => setField("phone", e.target.value)}
                                        className="mm-input"
                                        placeholder="(555) 123-4567"
                                        autoComplete="tel"
                                        data-testid="book-phone"
                                        required
                                    />
                                </Field>
                                <Field label="Topic" error={errors.topic}>
                                    <select
                                        value={form.topic}
                                        onChange={(e) => setField("topic", e.target.value)}
                                        className="mm-input"
                                        data-testid="book-topic"
                                    >
                                        {TOPICS.map((t) => <option key={t}>{t}</option>)}
                                    </select>
                                </Field>
                                <Field label="Preferred date *" error={errors.preferred_date}>
                                    <input
                                        type="date"
                                        min={minD}
                                        max={maxD}
                                        value={form.preferred_date}
                                        onChange={(e) => setField("preferred_date", e.target.value)}
                                        className="mm-input"
                                        data-testid="book-date"
                                        required
                                    />
                                </Field>
                                <Field label="Time window" error={errors.preferred_window}>
                                    <select
                                        value={form.preferred_window}
                                        onChange={(e) => setField("preferred_window", e.target.value)}
                                        className="mm-input"
                                        data-testid="book-window"
                                    >
                                        {WINDOWS.map((w) => <option key={w}>{w}</option>)}
                                    </select>
                                </Field>
                                <Field label="Timezone" className="sm:col-span-2">
                                    <TimezoneSelect
                                        value={form.timezone}
                                        onChange={(v) => setField("timezone", v)}
                                        testid="book-tz"
                                    />
                                </Field>
                                <Field label="Notes (optional)" error={errors.notes} className="sm:col-span-2">
                                    <textarea
                                        value={form.notes}
                                        onChange={(e) => setField("notes", e.target.value.slice(0, 500))}
                                        rows={4}
                                        className="mm-input resize-y"
                                        placeholder="Anything we should know before the call?"
                                        data-testid="book-notes"
                                    />
                                    <div className="mt-1 text-right text-[11px] text-mm-muted">
                                        {form.notes.length}/500
                                    </div>
                                </Field>
                            </div>
                            <div className="mt-6 flex flex-col items-start justify-between gap-3 border-t border-mm-line pt-5 sm:flex-row sm:items-center">
                                <p className="text-xs text-mm-muted max-w-md">
                                    Submitting requests an appointment — it doesn't auto-book a slot.
                                    A licensed advisor will personally confirm.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="mm-btn-pill inline-flex items-center gap-2 bg-mm-navy px-6 py-3 text-sm font-semibold text-white transition hover:bg-mm-blue disabled:opacity-60"
                                    data-testid="book-submit"
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <CalendarCheck size={16} />}
                                    Request appointment
                                </button>
                            </div>
                        </form>
                    )}

                    <aside className="space-y-4">
                        <div className="mm-card p-6">
                            <div className="mb-2 flex items-center gap-2 text-mm-navy">
                                <ShieldCheck size={18} /> <strong>What happens next</strong>
                            </div>
                            <ol className="ml-4 list-decimal space-y-2 text-sm text-mm-muted">
                                <li>An advisor reviews your request within one business day.</li>
                                <li>We confirm your time slot by email — or suggest the closest fit.</li>
                                <li>You join the call. No pressure, no obligations.</li>
                            </ol>
                        </div>
                        <div className="mm-card p-6">
                            <div className="mb-2 flex items-center gap-2 text-mm-navy">
                                <Sparkles size={18} /> <strong>Talked to Jarvis already?</strong>
                            </div>
                            <p className="text-sm text-mm-muted">
                                {sessionId
                                    ? "We'll attach your conversation so the advisor walks in fully briefed."
                                    : "Try Jarvis first to skip discovery questions on the call."}
                            </p>
                        </div>
                        <p className="mm-disclaimer">
                            Moving Mountains does not guarantee approval, rates, or outcomes.
                            We do not sell your data.
                        </p>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, error, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 block text-[12px] font-medium uppercase tracking-wider text-mm-muted">{label}</span>
            {children}
            {error ? (
                <span className="mt-1 block text-xs text-rose-700" data-testid="field-error">{error}</span>
            ) : null}
        </label>
    );
}
